/**
 * F-MM-N23 follow-up (F-CTRL-N69): which online providers a given order may be
 * paid through.
 *
 * A marketplace-settled order type (Foodpanda) gets an online sub-method named
 * after itself — the backend creates it in `paymentcfg.EnsureProvider` when the
 * type is saved, and `ProcessPayment` forces every payment on such an order
 * onto that provider. That lock only ran one way: the provider tile stayed
 * offered on Dine In / Takeaway tickets too, so a cashier could tag an ordinary
 * taxed-and-filed sale as "Foodpanda" and poison both the marketplace day-close
 * count and the platform settlement reconciliation.
 *
 * These helpers are the other direction: a marketplace provider belongs to its
 * own order type and nowhere else.
 *
 * Deliberately keyed on "a marketplace-settled order type of this name exists",
 * never on the provider's label alone. A tenant with the feature off, or one
 * who simply named a plain online provider "Foodpanda" without marking any
 * order type, keeps seeing it everywhere — nothing about their bills changes.
 */
import { isMarketplaceSettledType, type OrderTypeConfig } from '@/lib/orderTypeSettings'

/**
 * Frontend twin of backend `paymentcfg.Slugify`: lowercase, [a-z0-9] kept,
 * runs of space/_/- collapsed to one underscore, trimmed, max 50 chars
 * (the width of `payments.payment_sub_method`).
 */
export function slugifyProviderLabel(label: string): string {
  let out = ''
  let lastUnd = false
  for (const ch of String(label ?? '').trim().toLowerCase()) {
    if ((ch >= 'a' && ch <= 'z') || (ch >= '0' && ch <= '9')) {
      out += ch
      lastUnd = false
      continue
    }
    if ((ch === '_' || ch === ' ' || ch === '-') && out.length > 0 && !lastUnd) {
      out += '_'
      lastUnd = true
    }
  }
  out = out.replace(/_+/g, '_').replace(/^_+|_+$/g, '')
  if (out.length > 50) out = out.slice(0, 50).replace(/^_+|_+$/g, '')
  return out
}

/**
 * Slugs of the online providers that exist only to settle a marketplace order
 * type. Empty when the tenant feature flag is off — with the flag off no order
 * can be marketplace-settled, so no provider is reserved and every tile stays
 * available exactly as it was before F-MM-N23 shipped.
 */
export function marketplaceProviderSlugs(
  rows: OrderTypeConfig[],
  marketplaceSettledEnabled: boolean,
): ReadonlySet<string> {
  const set = new Set<string>()
  if (!marketplaceSettledEnabled) return set
  for (const row of rows) {
    if (!isMarketplaceSettledType(row)) continue
    const slug = slugifyProviderLabel(row.label)
    if (slug) set.add(slug)
  }
  return set
}

/**
 * The provider tiles to offer for one order.
 *
 * On a marketplace-settled order the caller shows the locked provider chip
 * rather than a picker, so the list is returned untouched. On every ordinary
 * order the reserved providers are removed: hidden, not disabled, because a
 * greyed tile only invites the cashier to wonder what they did wrong.
 */
export function onlineProvidersForOrder<T extends { slug: string }>(
  methods: readonly T[],
  reservedSlugs: ReadonlySet<string>,
  orderIsMarketplaceSettled: boolean,
): T[] {
  if (orderIsMarketplaceSettled || reservedSlugs.size === 0) return methods.slice()
  return methods.filter((m) => !reservedSlugs.has(m.slug))
}
